import { CommonModule } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { SESSION_STORAGE } from '@ng-web-apis/common';

import { AppConfigModule, AppConfigService, AppContext } from '../../../app-config';

@Component({
  selector: 'app-debug-api-root',
  imports: [AppConfigModule, ReactiveFormsModule, CommonModule],
  template: `
    @if (notProd) {
      <div class="space-y-4">
        <div class="relative">
          <div class="mb-3 rounded-100 bg-neutral-50 p-300 text-xs">
            <div class="mb-1 font-medium text-neutral-800">Current API Root URLs:</div>
            <div class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
              <span class="text-neutral-600">Api::V1:</span>
              <span class="font-mono text-neutral-800" data-testid="current-quote-v1-url">{{ currentQuoteV1UrlSignal() }}</span>
              <span class="text-neutral-600">Quoting Platform:</span>
              <span class="font-mono text-neutral-800" data-testid="current-quoting-platform-url">{{ currentQuotingPlatformUrlSignal() }}</span>
            </div>
          </div>

          <label class="input__label mb-200 block" for="api-root-url"> API Root URLs </label>
          <div class="flex items-center overflow-hidden rounded-100 border border-neutral-700 focus-within:ring-2 focus-within:ring-primary">
            <input
              id="api-root-url"
              [formControl]="apiRootControl"
              class="text-fs-sm flex-1 border-r bg-transparent p-300 outline-none"
              placeholder="https://rift-pr-1234-app.dev.kin.co"
              data-testid="enter-backend-api"
            />
          </div>
          <div class="form-help">
            <div>ex: https://rift-pr-1234-app.dev.kin.co</div>
            <div class="mt-1 text-xs text-neutral-600">Updates both quoteV1 (/api/v1) and quotingPlatform (/api/v1/shim) root URLs</div>
          </div>
        </div>

        <button (click)="updateApiRootURL()" [disabled]="!this.apiRootControl.valid" class="button button--secondary" data-testid="update-root-api">Update API Roots</button>
      </div>
    }
  `,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DebugApiRootComponent implements OnInit {
  @AppContext() private _appContext!: AppContext;
  public sessionStorage = inject(SESSION_STORAGE);
  public appConfigService = inject(AppConfigService);
  private originalContext = this.appConfigService.appContext;
  public currentContextSignal = signal(this.appConfigService.appContext);

  public notProd = this._appContext.notProd;

  public currentQuoteV1UrlSignal = signal<string>('');
  public currentQuotingPlatformUrlSignal = signal<string>('');

  public apiRootControl = new FormControl<string>('', Validators.required);

  ngOnInit() {
    const currentApiRoot = this.currentContextSignal()?.apiRoot?.quoteV1;
    if (currentApiRoot) {
      const rootURL = currentApiRoot.replace('/api/v1', '');
      this.apiRootControl.setValue(rootURL);
    }

    this.updateCurrentUrlSignals();
  }

  private updateCurrentUrlSignals() {
    const context = this.currentContextSignal();
    this.currentQuoteV1UrlSignal.set(context?.apiRoot?.quoteV1 || 'Not set');
    this.currentQuotingPlatformUrlSignal.set(context?.apiRoot?.quotingPlatform || 'Not set');
  }

  public updateApiRootURL() {
    if (this.apiRootControl.valid && !!this.apiRootControl.value) {
      const quoteV1Url = this.apiRootControl.value + '/api/v1';
      const quotingPlatformUrl = this.apiRootControl.value + '/api/v1/shim';

      this.sessionStorage.setItem('updatedApiRootQuoteV1', quoteV1Url);
      this.sessionStorage.setItem('updatedApiRootQuotingPlatform', quotingPlatformUrl);

      const newContext = {
        ...this.originalContext,
        apiRoot: {
          ...this.originalContext?.apiRoot,
          quoteV1: quoteV1Url,
          quotingPlatform: quotingPlatformUrl,
        },
      };

      this.appConfigService.overrideContext(newContext as unknown as AppContext);
      this.currentContextSignal.set(newContext as unknown as AppContext);

      this.updateCurrentUrlSignals();
    }
  }
}
